import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentRoutineFormState } from "../utils/hosted-agent-routines"
import type { HostedAgentFormErrors, HostedAgentFormState } from "../utils/hosted-agents"

import { AlertTriangle, CalendarClock, ChevronDown, Info, Plus, Trash2 } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { AgentDeliveryReceipts } from "@/features/agents/components/agent-delivery-receipts"
import { M365ConnectionCard } from "@/features/auth/components/m365-connection-card"

import { HostedAgentRoutineDeliveryEditor } from "./hosted-agent-routine-delivery-editor"
import { HostedAgentRoutineRunHistory } from "./hosted-agent-routine-run-history"
import { HostedAgentRoutineScheduleEditor } from "./hosted-agent-routine-schedule-editor"
import {
  createBlankHostedAgentRoutine,
  describeHostedAgentRoutineSchedule,
  HOSTED_AGENT_ROUTINE_NAME_MAX_LENGTH,
  HOSTED_AGENT_ROUTINE_PROMPT_MAX_LENGTH,
  HOSTED_AGENT_WEEKDAY_DEFAULTS,
  hostedAgentDelegatedToolsSelected,
  validateHostedAgentRoutine,
} from "../utils/hosted-agent-routines"

// Routines tab of the hosted-agent edit wizard (HERMES B1). Scheduled
// routines are stored on the agent document (`routines[]`) and saved through
// the ordinary form PUT; "Run now" and the run history read/write the
// dedicated ops endpoints. Scheduled M365 access requires the owner's
// explicit background connection; missing required access stops the run.

type RoutinesTabProps = {
  agentId: string | undefined
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
  errors: HostedAgentFormErrors
}

export function HostedAgentRoutinesTab({ agentId, form, patch, errors }: RoutinesTabProps) {
  const { t } = useTranslation()
  const delegatedTools = hostedAgentDelegatedToolsSelected(form.tools)

  const updateRoutine = (id: string, changes: Partial<HostedAgentRoutineFormState>) => {
    patch({
      routines: form.routines.map((routine) =>
        routine.id === id ? { ...routine, ...changes } : routine
      ),
    })
  }

  const replaceRoutine = (next: HostedAgentRoutineFormState) => {
    patch({ routines: form.routines.map((routine) => (routine.id === next.id ? next : routine)) })
  }

  const removeRoutine = (id: string) => {
    patch({ routines: form.routines.filter((routine) => routine.id !== id) })
  }

  const addRoutine = () => {
    patch({ routines: [...form.routines, createBlankHostedAgentRoutine()] })
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="size-4" />
        <AlertTitle>
          {t("adminDashboard.hostedAgents.routinesTab.title", {
            defaultValue: "Scheduled routines",
          })}
        </AlertTitle>
        <AlertDescription>
          {t("adminDashboard.hostedAgents.routinesTab.intro", {
            defaultValue:
              "Routines let the agent run itself on a schedule with a canned instruction. Scheduled runs execute with the agent's own tools (web, library, index search). Tools that require a personal sign-in — Microsoft 365 and similar — are not available in scheduled mode yet and are refused at run time.",
          })}
        </AlertDescription>
      </Alert>

      {delegatedTools.length > 0 ? (
        <Alert
          className="border-foreground bg-destructive text-destructive-foreground dark:border-foreground [&>svg]:text-destructive-foreground"
          variant="destructive"
        >
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.routinesTab.delegatedWarningTitle", {
              defaultValue: "Scheduled Microsoft 365 access requires a connection",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.routinesTab.delegatedWarningBody", {
              defaultValue:
                "Connect Microsoft 365 and explicitly enable the owner connection on each routine that uses {{tools}}. Missing required access stops the run; incomplete results must not be distributed as complete.",
              tools: delegatedTools.join(", "),
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <M365ConnectionCard preserveDraft />
      {agentId ? <AgentDeliveryReceipts agentId={agentId} /> : null}

      {errors.routines ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
          {t("adminDashboard.hostedAgents.routinesTab.formError", {
            defaultValue: "One or more routines are incomplete — fix the highlighted fields.",
          })}
        </p>
      ) : null}

      {form.routines.length === 0 ? (
        <div className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          {t("adminDashboard.hostedAgents.routinesTab.empty", {
            defaultValue: "No routines yet. Add one to have the agent run on a schedule.",
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {form.routines.map((routine) => (
            <RoutineCard
              key={routine.id}
              agentId={agentId}
              routine={routine}
              onChange={(changes) => updateRoutine(routine.id, changes)}
              onRemove={() => removeRoutine(routine.id)}
              onReplace={replaceRoutine}
            />
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={addRoutine}>
        <Plus className="size-4" />
        {t("adminDashboard.hostedAgents.routinesTab.add", { defaultValue: "Add routine" })}
      </Button>
    </div>
  )
}

function RoutineCard({
  agentId,
  routine,
  onChange,
  onReplace,
  onRemove,
}: {
  agentId: string | undefined
  routine: HostedAgentRoutineFormState
  onChange: (changes: Partial<HostedAgentRoutineFormState>) => void
  onReplace: (next: HostedAgentRoutineFormState) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(!routine.name.trim())
  const fieldErrors = validateHostedAgentRoutine(routine)

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-3 p-3">
        <button
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          type="button"
          onClick={() => setExpanded((value) => !value)}
        >
          <ChevronDown
            className={`text-muted-foreground size-4 shrink-0 transition-transform ${
              expanded ? "" : "-rotate-90"
            }`}
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">
                {routine.name.trim() ||
                  t("adminDashboard.hostedAgents.routinesTab.unnamed", {
                    defaultValue: "Untitled routine",
                  })}
              </span>
              <RoutineStatusBadge enabled={routine.enabled} hasErrors={fieldErrors.length > 0} />
            </span>
            <span className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
              <CalendarClock className="size-3" />
              <RoutineScheduleSummary routine={routine} />
            </span>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          <Switch
            checked={routine.enabled}
            aria-label={t("adminDashboard.hostedAgents.routinesTab.enabledLabel", {
              defaultValue: "Enabled",
            })}
            onCheckedChange={(enabled) => onChange({ enabled })}
          />
          <RoutineDeleteButton onRemove={onRemove} />
        </div>
      </div>

      {expanded ? (
        <div className="space-y-4 border-t p-3">
          <div className="space-y-2">
            <Label htmlFor={`routine-name-${routine.id}`}>
              {t("adminDashboard.hostedAgents.routinesTab.nameLabel", { defaultValue: "Name" })}{" "}
              <span className="text-foreground">*</span>
            </Label>
            <Input
              aria-invalid={fieldErrors.includes("name")}
              id={`routine-name-${routine.id}`}
              maxLength={HOSTED_AGENT_ROUTINE_NAME_MAX_LENGTH}
              value={routine.name}
              placeholder={t("adminDashboard.hostedAgents.routinesTab.namePlaceholder", {
                defaultValue: "e.g. Daily tender digest",
              })}
              onChange={(event) => onChange({ name: event.target.value })}
            />
          </div>

          <HostedAgentRoutineScheduleEditor routine={routine} onReplace={onReplace} />

          <div className="space-y-2">
            <Label htmlFor={`routine-prompt-${routine.id}`}>
              {t("adminDashboard.hostedAgents.routinesTab.promptLabel", {
                defaultValue: "Instruction",
              })}{" "}
              <span className="text-foreground">*</span>
            </Label>
            <Textarea
              aria-invalid={fieldErrors.includes("prompt")}
              id={`routine-prompt-${routine.id}`}
              maxLength={HOSTED_AGENT_ROUTINE_PROMPT_MAX_LENGTH}
              rows={4}
              value={routine.prompt}
              placeholder={t("adminDashboard.hostedAgents.routinesTab.promptPlaceholder", {
                defaultValue: "The message the agent runs on each scheduled turn…",
              })}
              onChange={(event) => onChange({ prompt: event.target.value })}
            />
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.hostedAgents.routinesTab.promptHelp", {
                defaultValue:
                  "This text is sent to the agent as the user turn each time the routine fires.",
              })}
            </p>
          </div>

          <HostedAgentRoutineDeliveryEditor
            agentId={agentId}
            routine={routine}
            onChange={onChange}
          />
          <HostedAgentRoutineRunHistory agentId={agentId} routine={routine} />
        </div>
      ) : null}
    </div>
  )
}

function RoutineStatusBadge({ hasErrors, enabled }: { hasErrors: boolean; enabled: boolean }) {
  const { t } = useTranslation()
  if (hasErrors) {
    return (
      <Badge variant="destructive">
        {t("adminDashboard.hostedAgents.routinesTab.incomplete", { defaultValue: "Incomplete" })}
      </Badge>
    )
  }
  if (!enabled) {
    return (
      <Badge variant="secondary">
        {t("adminDashboard.hostedAgents.routinesTab.paused", { defaultValue: "Paused" })}
      </Badge>
    )
  }
  return null
}

function RoutineDeleteButton({ onRemove }: { onRemove: () => void }) {
  const { t } = useTranslation()
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("adminDashboard.hostedAgents.routinesTab.delete", {
            defaultValue: "Delete routine",
          })}
        >
          <Trash2 className="text-foreground size-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("adminDashboard.hostedAgents.routinesTab.deleteTitle", {
              defaultValue: "Delete this routine?",
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("adminDashboard.hostedAgents.routinesTab.deleteDescription", {
              defaultValue:
                "The routine is removed when you save the agent. This cannot be undone.",
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t("adminDashboard.hostedAgents.routinesTab.deleteCancel", { defaultValue: "Cancel" })}
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onRemove}
          >
            {t("adminDashboard.hostedAgents.routinesTab.deleteConfirm", { defaultValue: "Delete" })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function RoutineScheduleSummary({ routine }: { routine: HostedAgentRoutineFormState }) {
  const { t } = useTranslation()
  const summary = describeHostedAgentRoutineSchedule(routine)
  if (summary.kind === "interval") {
    return (
      <>
        {t("adminDashboard.hostedAgents.routinesTab.summaryInterval", {
          defaultValue: "Every {{minutes}} min",
          minutes: summary.minutes,
        })}
      </>
    )
  }
  if (summary.kind === "daily") {
    return (
      <>
        {t("adminDashboard.hostedAgents.routinesTab.summaryDaily", {
          defaultValue: "Daily at {{time}} UTC",
          time: summary.time,
        })}
      </>
    )
  }
  if (summary.kind === "weekly") {
    return (
      <>
        {t("adminDashboard.hostedAgents.routinesTab.summaryWeekly", {
          defaultValue: "Weekly, {{day}} at {{time}} UTC",
          day: t(`adminDashboard.hostedAgents.routinesTab.weekday.${summary.weekday}`, {
            defaultValue: HOSTED_AGENT_WEEKDAY_DEFAULTS[summary.weekday] ?? summary.weekday,
          }),
          time: summary.time,
        })}
      </>
    )
  }
  if (summary.kind === "cron") {
    return (
      <>
        {t("adminDashboard.hostedAgents.routinesTab.summaryCron", {
          defaultValue: "Cron: {{cron}}",
          cron: summary.cron,
        })}
      </>
    )
  }
  return (
    <>
      {t("adminDashboard.hostedAgents.routinesTab.summaryUnset", {
        defaultValue: "No schedule set",
      })}
    </>
  )
}
